import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { RootState } from "../../../../redux/store";
import {
  checkInDateSelected,
  checkOutDateSelected,
  setNextEnabled,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { EquipmentListProps } from "../../../../types/baseTypes";

type KeyHandoverScheduleContextValue = {
  // State
  orderData: RootState["bookingCreation"]["orderData"];
  availabilities: RootState["bookingCreation"]["availabilities"];
  selectedGroup: EquipmentListProps | null;
  currency: string;
  nextEnabled: boolean;
  isLoading: boolean;
  // Actions
  setCheckIn: (date: moment.Moment | null) => void;
  setCheckOut: (date: moment.Moment | null) => void;
  setDates: (checkIn: moment.Moment | null, checkOut: moment.Moment | null) => void;
  setNextEnabled: (enabled: boolean) => void;
};

const KeyHandoverScheduleContext = createContext<KeyHandoverScheduleContextValue | undefined>(
  undefined
);

export function KeyHandoverScheduleProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();

  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);
  const availabilities = useSelector((state: RootState) => state.bookingCreation.availabilities);
  const selectedGroup = useSelector(
    (state: RootState) => state.bookingCreation.selectedGroup
  ) as EquipmentListProps | null;
  const currency =
    useSelector((state: RootState) => state.tenantConfig.config.currency) || "EUR";
  const nextEnabled = useSelector((state: RootState) => state.bookingCreation.nextEnabled);

  const setCheckIn = useCallback(
    (date: moment.Moment | null) => {
      dispatch(checkInDateSelected({ checkInDate: date }));
      const hasBoth = Boolean(date && orderData.checkOutDate);
      dispatch(setNextEnabled(hasBoth));
    },
    [dispatch, orderData.checkOutDate]
  );

  const setCheckOut = useCallback(
    (date: moment.Moment | null) => {
      dispatch(checkOutDateSelected({ checkOutDate: date }));
      const hasBoth = Boolean(orderData.checkInDate && date);
      dispatch(setNextEnabled(hasBoth));
    },
    [dispatch, orderData.checkInDate]
  );

  const setDates = useCallback(
    (checkIn: moment.Moment | null, checkOut: moment.Moment | null) => {
      dispatch(checkInDateSelected({ checkInDate: checkIn }));
      dispatch(checkOutDateSelected({ checkOutDate: checkOut }));
      dispatch(setNextEnabled(Boolean(checkIn && checkOut)));
    },
    [dispatch]
  );

  const value = useMemo<KeyHandoverScheduleContextValue>(
    () => ({
      orderData,
      availabilities,
      selectedGroup,
      currency,
      nextEnabled,
      isLoading: !Boolean(availabilities),
      setCheckIn,
      setCheckOut,
      setDates,
      setNextEnabled: (enabled: boolean) => dispatch(setNextEnabled(enabled)),
    }),
    [
      orderData,
      availabilities,
      selectedGroup,
      currency,
      nextEnabled,
      setCheckIn,
      setCheckOut,
      setDates,
      dispatch,
    ]
  );

  return (
    <KeyHandoverScheduleContext.Provider value={value}>
      {children}
    </KeyHandoverScheduleContext.Provider>
  );
}

export function useKeyHandoverSchedule() {
  const ctx = useContext(KeyHandoverScheduleContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverSchedule must be used within a KeyHandoverScheduleProvider"
    );
  }
  return ctx;
}
